import * as React from 'react';
import { Link } from 'react-router-dom';
import { SetupCardFunction } from '../../../stores';
import { AsyncLoader } from '../../Common/AsyncLoader';
import { LinkButton } from '../../Common/LinkButton';
import { Checkout } from '../Checkout';
import { PaymentForm } from '../Checkout/PaymentForm';
import { LoginRequiredSection } from './LoginRequiredSection';
import { PremiumContentInfo } from './PremiumContentInfo';
import { PricingInfo } from './PricingInfo';
import './style.css';

export interface PremiumPageProps {
  error?: Error;
  isAlreadyAMember: boolean;
  isLoggedIn: boolean;
  isSubscribed: boolean;
  loading: boolean;
  monthlyPrice?: number;
  onSubscribe: (setupCard: SetupCardFunction) => Promise<void>;
  pageUrl: string;
  paymentError?: Error;
  paymentRequestRunning?: boolean;
  redirectAfterSuccessUrl?: string;
}

export interface PremiumPageState {
  /* empty*/
}

export class PremiumPage extends React.Component<
  PremiumPageProps,
  PremiumPageState
> {
  private paymentFormRef = React.createRef<{
    getWrappedInstance(): PaymentForm | undefined;
  }>();

  render() {
    const { error, loading, monthlyPrice, pageUrl } = this.props;

    return (
      <AsyncLoader
        className="premium-page-loader"
        error={!!error}
        loading={loading}
        pageUrl={pageUrl}
      >
        <main className="premium-page plain-content-page" role="main">
          <h1 className="premium-page__heading plain-content-page-header">
            Premier membership
          </h1>
          <div className="premium-page__main-content-wrapper">
            <PremiumContentInfo className="premium-page__premium-content-info" />
            <div className="premium-page__main-content">
              <PricingInfo
                className="premium-page__pricing-info"
                monthlyPrice={monthlyPrice as number}
              />
              {this.getMainPageContent()}
            </div>
          </div>
        </main>
      </AsyncLoader>
    );
  }

  private getMainPageContent = () => {
    const { isAlreadyAMember, isLoggedIn, isSubscribed } = this.props;

    if (!isLoggedIn) {
      return <LoginRequiredSection />;
    }

    if (isSubscribed) {
      return this.getSubscriptionSuccessMessage();
    }

    if (isAlreadyAMember) {
      return this.getAlreadyAMemberMessage();
    }

    return this.getCardEntryForm();
  };

  private getCardEntryForm = () => {
    const { onSubscribe, paymentError, paymentRequestRunning } = this.props;

    return (
      <section>
        <Checkout
          className="premium-page__card-entry-form"
          forwardRef={this.paymentFormRef}
          onSubmit={async setupCard => {
            try {
              await onSubscribe(setupCard);

              // Clear the card entry form if we can
              if (this.paymentFormRef.current) {
                const paymentForm = this.paymentFormRef.current.getWrappedInstance();

                if (paymentForm) {
                  paymentForm.clearInputs();
                }
              }
            } catch (ex) {
              // Ignore errors - it gets handled by the error flag in the store
            }
          }}
          paymentError={paymentError}
          paymentRequestRunning={paymentRequestRunning}
        />
      </section>
    );
  };

  private getSubscriptionSuccessMessage = () => {
    const { redirectAfterSuccessUrl } = this.props;

    return (
      <section className="premium-page__sign-up-success">
        <h3 className="premium-page__sign-up-success-heading">
          Sign up complete!
        </h3>
        <p className="premium-page__sign-up-success-message">
          You can start enjoying the benefits of your Edge of Play Premier
          membership immediately
        </p>
        {redirectAfterSuccessUrl ? (
          <Link
            className="premium-page__sign-up-success-return-to-previous-page-link"
            to={redirectAfterSuccessUrl}
          >
            Great, take me back to where I came from
          </Link>
        ) : null}
      </section>
    );
  };

  private getAlreadyAMemberMessage = () => {
    return (
      <section className="premium-page__already-a-member">
        <h3 className="premium-page__already-a-member-heading">
          You're already a premier member!
        </h3>
        <p>
          Thanks for signing up for Edge of Play Premier and supporting the
          site.
        </p>
        <p>
          To manage your membership please <br />
          <br />
          <LinkButton to={'/profile'}>Visit your profile</LinkButton>
        </p>
      </section>
    );
  };
}

export default PremiumPage;



// WEBPACK FOOTER //
// ./src/app/components/Premium/PremiumPage/index.tsx